import { fetchUser } from '@/lib/api';
import { UserSchema } from '@/lib/types';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router';
import axiosInstance from '@/lib/axios';
import { getAuth0ApiAuthorizationParams } from '@/constants/auth0Api';
import { ga } from '@/lib/analytics';
import {
  consumePostLoginRedirect,
  storeCurrentPostLoginRedirect,
} from '@/lib/postLoginRedirect';
import {
  getLoginRejectionReason,
  LoginRejectionReason,
  storeLoginRejection,
} from '@/lib/loginRejection';
import { useAppLogout } from '@/hooks/useAppLogout';

const isConsentOrLoginRequired = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const e = err as { error?: string; message?: string };
  const code = e.error?.toLowerCase();
  if (code === 'consent_required' || code === 'login_required') {
    return true;
  }
  const msg = (
    typeof e.message === 'string' ? e.message : ''
  ).toLowerCase();
  return (
    msg.includes('consent required') || msg.includes('login required')
  );
};

const AuthContext = createContext<{
  user: UserSchema | undefined;
  isPending: boolean;
}>({
  user: undefined,
  isPending: false,
});

export const useAuthContext = () => useContext(AuthContext);

const AuthContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginEventSentRef = useRef(false);
  const silentAuthAttemptedRef = useRef(false);
  const loginRejectedRef = useRef(false);
  const appLogout = useAppLogout();
  const [silentAuthAttempted, setSilentAuthAttempted] =
    useState(false);
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    isLoading: isAuth0Loading,
    isAuthenticated,
    user: auth0User,
  } = useAuth0();
  const {
    isFetching: isUserPending,
    isError: isUserFetchError,
    data,
    refetch,
  } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: 0,
    enabled: false,
  });
  const isEmailNotVerifiedPage =
    location.pathname === '/email-not-verified';
  const isPending =
    (!isEmailNotVerifiedPage && isUserPending) ||
    (!isEmailNotVerifiedPage && isAuth0Loading) ||
    (!isEmailNotVerifiedPage &&
      !isAuth0Loading &&
      !silentAuthAttempted) ||
    (!isEmailNotVerifiedPage &&
      !isAuth0Loading &&
      isAuthenticated &&
      !data &&
      !isUserFetchError);

  useEffect(() => {
    if (isEmailNotVerifiedPage) {
      return;
    }
    // Important: after refresh, `isAuthenticated` can be false even if a valid
    // Auth0 session cookie exists. We do ONE silent token attempt before
    // allowing the app to redirect to `/login`.
    if (!isAuth0Loading && !silentAuthAttemptedRef.current) {
      silentAuthAttemptedRef.current = true;
      getAccessTokenSilently({
        authorizationParams: getAuth0ApiAuthorizationParams(),
      }).then(
        (accessToken) => {
          axiosInstance.defaults.headers.common['Authorization'] =
            `Bearer ${accessToken}`;
          setSilentAuthAttempted(true);
          void refetch();
        },
        () => {
          delete axiosInstance.defaults.headers.common[
            'Authorization'
          ];
          setSilentAuthAttempted(true);
        },
      );
      return;
    }

    if (!isAuthenticated) {
      delete axiosInstance.defaults.headers.common['Authorization'];
      return;
    }
    getAccessTokenSilently({
      authorizationParams: getAuth0ApiAuthorizationParams(),
    }).then(
      (accessToken) => {
        axiosInstance.defaults.headers.common['Authorization'] =
          `Bearer ${accessToken}`;
        void refetch();
      },
      (error) => {
        if (isConsentOrLoginRequired(error)) {
          loginWithRedirect({
            authorizationParams: {
              ...getAuth0ApiAuthorizationParams(),
              prompt: 'consent',
            },
          });
          return;
        }
        if (!isAuthenticated) {
          delete axiosInstance.defaults.headers.common[
            'Authorization'
          ];
          return;
        }
        void refetch();
      },
    );
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    isAuthenticated,
    isEmailNotVerifiedPage,
    refetch,
    isAuth0Loading,
    silentAuthAttempted,
  ]);

  useEffect(() => {
    if (
      !isEmailNotVerifiedPage &&
      isAuthenticated &&
      data &&
      !loginEventSentRef.current
    ) {
      const userRole = data.roles?.[0]?.name || 'Unknown';

      ga.setUserId(auth0User?.sub);
      ga.trackEvent('login_success', {
        user_role: userRole,
      });

      loginEventSentRef.current = true;
    }

    // Deliberately not guarded by loginEventSentRef: consuming the stored
    // path clears it, so this is once-only on its own, and the two must not
    // share state. `data` rather than `isAuthenticated` alone - the app
    // gates rendering on the user being fetched, not just authenticated.
    if (!isEmailNotVerifiedPage && isAuthenticated && data) {
      const redirectPath = consumePostLoginRedirect();
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }
    }

    if (!isAuthenticated) {
      loginEventSentRef.current = false;
    }
  }, [
    data,
    isAuthenticated,
    isEmailNotVerifiedPage,
    auth0User,
    navigate,
  ]);

  useEffect(() => {
    // The backend refuses a signed-in user it never invited, or one who was
    // deactivated. Their Auth0 session is still valid, so it has to be
    // ended here, or the next login would let them straight back in to be
    // refused again. Parallel requests all fail the same way; act once.
    const rejectLogin = (reason: LoginRejectionReason) => {
      if (loginRejectedRef.current) {
        return;
      }
      loginRejectedRef.current = true;
      delete axiosInstance.defaults.headers.common['Authorization'];
      // Refused on an invitation link: keep the invitation, so the user can
      // still accept it with the account it was meant for. `window.location`
      // is the live URL; this closure outlives react-router's `location`.
      const current = new URLSearchParams(window.location.search);
      const params = new URLSearchParams({ reason });
      for (const key of ['invitation', 'organization']) {
        const value = current.get(key);
        if (value) params.set(key, value);
      }
      // Storage carries the reason through Auth0's logout, whose returnTo
      // must be the plain /login URL. The in-app navigation shows it right
      // away and keeps SecurePage from bouncing to a plain /login meanwhile.
      storeLoginRejection({
        reason,
        invitation: params.get('invitation') ?? undefined,
        organization: params.get('organization') ?? undefined,
      });
      navigate(`/login?${params}`, { replace: true });
      appLogout();
    };

    const id = axiosInstance.interceptors.response.use(
      (response) => {
        if (
          !loginRejectedRef.current &&
          response.config.url !== '/api/me' &&
          response.status === 401
        ) {
          // A session that expired mid-visit is still a deep link worth
          // returning to, same as an interrupted navigation.
          storeCurrentPostLoginRedirect();
          navigate('/login');
          return response;
        }
        return response;
      },
      async (error) => {
        // Already logging out over a refused sign-in: a later 401 must not
        // navigate to a plain /login, whose auto-login would race the logout.
        if (loginRejectedRef.current) {
          return Promise.reject(error);
        }
        const rejectionReason = getLoginRejectionReason(
          error.response?.status,
          error.response?.data,
        );
        if (rejectionReason) {
          rejectLogin(rejectionReason);
          return Promise.reject(error);
        }
        if (
          error.response?.config.url === '/api/me' &&
          error.response?.status === 403 &&
          error.response?.data?.error_code === 21
        ) {
          navigate('/email-not-verified');
          return Promise.reject(error);
        }
        if (
          error.response?.config.url !== '/api/me' &&
          error.response?.status === 401
        ) {
          storeCurrentPostLoginRedirect();
          navigate('/login');
          return error;
        }
        return Promise.reject(error);
      },
    );
    return () => {
      axiosInstance.interceptors.response.eject(id);
    };
  }, [navigate, appLogout]);

  return (
    <AuthContext.Provider value={{ user: data, isPending }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthContextProvider;
